import { createSlice } from '@reduxjs/toolkit';
import * as R from 'ramda';
import { getStringifiedQueryParams } from 'utils/helpers/fetch';
import http from 'utils/http';
import { loadingManagementApi } from 'feature/loading-management/loadingManagementApi';
import { fetchAuthorizationSequencingCards } from '../../store/authorizationSequencingSlice';

const initialState = {
  futureSequences: [],
  inventoryOwners: [],
  inventoryOwnersLoading: false,
  locations: [],
  locationsLoading: false,
  products: [],
  productsLoading: false,
  submittingBatch: false,
};

const reducers = {
  resetSequencingWizard: R.always(initialState),
  setFutureSequences: R.assoc('futureSequences'),
  setInventoryOwners: R.assoc('inventoryOwners'),
  setInventoryOwnersLoading: R.assoc('inventoryOwnersLoading'),
  setLocations: R.assoc('locations'),
  setLocationsLoading: R.assoc('locationsLoading'),
  setProducts: R.assoc('products'),
  setProductsLoading: R.assoc('productsLoading'),
  setSubmittingBatch: R.assoc('submittingBatch'),
};

const { actions, reducer } = createSlice({
  initialState,
  name: 'sequencingWizard',
  // eslint-disable-next-line react-hooks/rules-of-hooks
  reducers: R.map(singleReducer => R.flip(R.useWith(singleReducer, [R.prop('payload')])), reducers),
});

export const {
  resetSequencingWizard,
  setFutureSequences,
  setInventoryOwners,
  setInventoryOwnersLoading,
  setLocations,
  setLocationsLoading,
  setProducts,
  setProductsLoading,
  setSubmittingBatch,
} = actions;

// Selectors
const mergePath = R.concat(['loadingManagement', 'sequencingWizard']);
export const getStoreValueByProp = R.o(R.path, mergePath);

const refreshAuthorizationSequencing =
  ({ companyCustomerIdName, currentSelection, dataCompanyId, viewBy }) =>
  dispatch => {
    if (!viewBy) {
      dispatch(loadingManagementApi.util.invalidateTags(['AuthorizationSequencing']));
      return;
    }
    dispatch(
      fetchAuthorizationSequencingCards(
        {
          supplier: { companyCustomerIdName, dataCompanyId },
          [`${viewBy}Id`]: currentSelection?.[`${viewBy}Id`],
        },
        viewBy,
        true
      )
    );
  };

export const fetchInventoryOwners = params => dispatch => {
  const { companyCustomerIdName, dataCompanyId } = params;
  dispatch(setInventoryOwnersLoading(true));

  const queryString = getStringifiedQueryParams({
    supplier: {
      companyCustomerIdName,
      dataCompanyId,
    },
  });
  http
    .get(`authorization-sequencing/get-supplier-inventory-owners?${queryString}`)
    .then(({ data }) => {
      dispatch(setInventoryOwners(data));
    })
    .finally(() => dispatch(setInventoryOwnersLoading(false)));
};

export const fetchLocations = body => dispatch => {
  dispatch(setLocations([]));
  dispatch(setLocationsLoading(true));
  http
    .post('authorization-sequencing/get-product-locations/', body)
    .then(({ data }) => {
      dispatch(setLocations(data));
    })
    .finally(() => dispatch(setLocationsLoading(false)));
};

export const fetchProducts = params => dispatch => {
  const { companyCustomerIdName, dataCompanyId, inventoryOwners } = params;
  dispatch(setProducts([]));
  dispatch(setProductsLoading(true));

  const body = {
    inventoryOwners,
    supplier: {
      companyCustomerIdName,
      dataCompanyId,
    },
  };
  // looks like a POST but is actually a GET
  http
    .post('authorization-sequencing/get-inventory-owner-products/', body)
    .then(({ data }) => {
      dispatch(setProducts(data));
    })
    .finally(() => dispatch(setProductsLoading(false)));
};

export const fetchFutureSequences = params => dispatch => {
  const { companyCustomerIdName, dataCompanyId, inventoryOwners, locations, products } = params;

  const inventoryOwnerSequence = R.pluck('companyCustomerIdName', inventoryOwners);
  const productIds = R.pluck('id', products);
  const locationIds = R.pluck('id', locations);
  const body = {
    inventoryOwners: inventoryOwnerSequence,
    locations: locationIds,
    products: productIds,
    supplier: {
      companyCustomerIdName,
      dataCompanyId,
    },
  };

  http
    .post('authorization-sequencing/get-future-sequences/', body)
    .then(({ data }) => dispatch(setFutureSequences(data)));
};

export const removeBatchSequence = params => dispatch => {
  const {
    dataCompanyId,
    companyCustomerIdName,
    currentSelection,
    futureSequenceDate,
    inventoryOwners,
    products,
    locations,
    viewBy,
  } = params;

  const pluckedInventoryOwners = R.pluck('companyCustomerIdName', inventoryOwners);
  const productIds = R.pluck('id', products);
  const locationIds = R.pluck('id', locations);
  const body = {
    futureSequenceDate,
    inventoryOwners: pluckedInventoryOwners,
    locationIds,
    productIds,
    supplier: {
      companyCustomerIdName,
      dataCompanyId,
    },
  };

  return http
    .post('authorization-sequencing/batch-remove-authorization-sequences', body)
    .then(({ data }) => data)
    .finally(() => {
      dispatch(
        refreshAuthorizationSequencing({
          companyCustomerIdName,
          currentSelection,
          dataCompanyId,
          viewBy,
        })
      );
    });
};

export const editBatchApplySequence = params => dispatch => {
  const {
    dataCompanyId,
    companyCustomerIdName,
    currentSelection,
    futureSequenceDate,
    inventoryOwners,
    products,
    locations,
    action,
    viewBy,
  } = params;

  const inventoryOwnerSequence = R.pluck('companyCustomerIdName', inventoryOwners);
  const productIds = R.pluck('id', products);
  const locationIds = R.pluck('id', locations);
  const body = {
    action,
    futureSequenceDate,
    inventoryOwnerSequence,
    locationIds,
    productIds,
    supplier: {
      companyCustomerIdName,
      dataCompanyId,
    },
  };

  return http
    .post('authorization-sequencing/edit-batch-authorization-sequences', body)
    .then(({ data }) => data)
    .finally(() => {
      dispatch(
        refreshAuthorizationSequencing({
          companyCustomerIdName,
          currentSelection,
          dataCompanyId,
          viewBy,
        })
      );
    });
};

export default reducer;
